import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route, Navigate } from "react-router-dom";
import CountryBlock from "@/components/CountryBlock";
import ScrollToTop from "@/components/ScrollToTop";
const Index = lazy(() => import("./pages/Index"));
const Students = lazy(() => import("./pages/Students"));
const StudentsCn = lazy(() => import("./pages/students/StudentsCn"));
const StudentsFr = lazy(() => import("./pages/students/StudentsFr"));
const StudentsJp = lazy(() => import("./pages/students/StudentsJp"));
const StudentsKr = lazy(() => import("./pages/students/StudentsKr"));
const StudentsTw = lazy(() => import("./pages/students/StudentsTw"));
const StudentsVi = lazy(() => import("./pages/students/StudentsVi"));
const RedesignPreview = lazy(() => import("./pages/RedesignPreview"));
const HomeCanadaPreview = lazy(() => import("./pages/HomeCanadaPreview"));
const IndexFr = lazy(() => import("./pages/IndexFr"));
const IndexVi = lazy(() => import("./pages/IndexVi"));
const IndexTw = lazy(() => import("./pages/IndexTw"));
const IndexKr = lazy(() => import("./pages/IndexKr"));
const IndexJp = lazy(() => import("./pages/IndexJp"));
const IndexCn = lazy(() => import("./pages/IndexCn"));
const OnlineWorkspace = lazy(() => import("./pages/OnlineWorkspace"));
const ProductivityTool = lazy(() => import("./pages/ProductivityTool"));
const StudyStreamPage = lazy(() => import("./pages/StudyStream"));
const StudyStreamRedirect = lazy(() => import("./pages/StudyStreamRedirect"));
const WhyStudyingOnCameraHelps = lazy(() => import("./pages/study-stream/WhyStudyingOnCameraHelps"));
const GongbangToGlobal = lazy(() => import("./pages/study-stream/GongbangToGlobal"));
const TechToolkitSuccess = lazy(() => import("./pages/study-stream/TechToolkitSuccess"));
const ProductivityHacks = lazy(() => import("./pages/study-stream/ProductivityHacks"));
const StudyStreamVsStudyGroup = lazy(() => import("./pages/study-stream/StudyStreamVsStudyGroup"));
const StudyingAloneTogether = lazy(() => import("./pages/study-stream/StudyingAloneTogether"));
const Pomodoro20 = lazy(() => import("./pages/study-stream/Pomodoro20"));
const PomodoroTechnique = lazy(() => import("./pages/PomodoroTechnique"));
const Pomodoro101 = lazy(() => import("./pages/pomodoro/Pomodoro101"));
const ScienceOfFocus = lazy(() => import("./pages/pomodoro/ScienceOfFocus"));
const DoesItWork = lazy(() => import("./pages/pomodoro/DoesItWork"));
const PomodoroVsProcrastination = lazy(() => import("./pages/pomodoro/PomodoroVsProcrastination"));
const StudyHabits = lazy(() => import("./pages/pomodoro/StudyHabits"));
const AvoidBurnout = lazy(() => import("./pages/pomodoro/AvoidBurnout"));
const BestTools = lazy(() => import("./pages/pomodoro/BestTools"));
const StudyStreams = lazy(() => import("./pages/pomodoro/StudyStreams"));
const CustomizePomodoro = lazy(() => import("./pages/pomodoro/CustomizePomodoro"));
const WhyBreaksMatter = lazy(() => import("./pages/pomodoro/WhyBreaksMatter"));
const ExamPrep = lazy(() => import("./pages/pomodoro/ExamPrep"));
const ActiveLearning = lazy(() => import("./pages/pomodoro/ActiveLearning"));
const PomodoroADHD = lazy(() => import("./pages/pomodoro/PomodoroADHD"));
const Reminders = lazy(() => import("./pages/Reminders"));
const StudyTimer = lazy(() => import("./pages/StudyTimer"));
const TaskManagement = lazy(() => import("./pages/TaskManagement"));
const TasksRedirect = lazy(() => import("./pages/TasksRedirect"));
const Quizzes = lazy(() => import("./pages/Quizzes"));
const FlashCards = lazy(() => import("./pages/FlashCards"));
const StudyRoom = lazy(() => import("./pages/StudyRoom"));
// Study Room university pages - Toronto
const UofTStGeorge = lazy(() => import("./pages/study-room/UofTStGeorge"));
const UofTMississauga = lazy(() => import("./pages/study-room/UofTMississauga"));
const UofTScarborough = lazy(() => import("./pages/study-room/UofTScarborough"));
const YorkUniversity = lazy(() => import("./pages/study-room/YorkUniversity"));
const TorontoMetropolitanUniversity = lazy(() => import("./pages/study-room/TorontoMetropolitanUniversity"));
const OCADUniversity = lazy(() => import("./pages/study-room/OCADUniversity"));
const GuelphHumber = lazy(() => import("./pages/study-room/GuelphHumber"));
const SenecaCollege = lazy(() => import("./pages/study-room/SenecaCollege"));
const HumberCollege = lazy(() => import("./pages/study-room/HumberCollege"));
// Study Room university pages - Canada
const McGillUniversity = lazy(() => import("./pages/study-room/McGillUniversity"));
const UBCVancouver = lazy(() => import("./pages/study-room/UBCVancouver"));
const UniversityOfAlberta = lazy(() => import("./pages/study-room/UniversityOfAlberta"));
const UniversityOfCalgary = lazy(() => import("./pages/study-room/UniversityOfCalgary"));
const UniversityOfOttawa = lazy(() => import("./pages/study-room/UniversityOfOttawa"));
const McMasterUniversity = lazy(() => import("./pages/study-room/McMasterUniversity"));
const UniversityOfWaterloo = lazy(() => import("./pages/study-room/UniversityOfWaterloo"));
const WesternUniversity = lazy(() => import("./pages/study-room/WesternUniversity"));
const QueensUniversity = lazy(() => import("./pages/study-room/QueensUniversity"));
const UniversityOfManitoba = lazy(() => import("./pages/study-room/UniversityOfManitoba"));
const UniversityOfSaskatchewan = lazy(() => import("./pages/study-room/UniversityOfSaskatchewan"));
const UniversiteLaval = lazy(() => import("./pages/study-room/UniversiteLaval"));
const UniversiteDeMontreal = lazy(() => import("./pages/study-room/UniversiteDeMontreal"));
const DalhousieUniversity = lazy(() => import("./pages/study-room/DalhousieUniversity"));
const SimonFraserUniversity = lazy(() => import("./pages/study-room/SimonFraserUniversity"));
const ConcordiaUniversity = lazy(() => import("./pages/study-room/ConcordiaUniversity"));
const UniversityOfVictoria = lazy(() => import("./pages/study-room/UniversityOfVictoria"));
const MemorialUniversity = lazy(() => import("./pages/study-room/MemorialUniversity"));
const CarletonUniversity = lazy(() => import("./pages/study-room/CarletonUniversity"));
const UniversityOfNewBrunswick = lazy(() => import("./pages/study-room/UniversityOfNewBrunswick"));
// Study Room university pages - USA
const UniversityOfCentralFlorida = lazy(() => import("./pages/study-room/UniversityOfCentralFlorida"));
const TexasAMUniversity = lazy(() => import("./pages/study-room/TexasAMUniversity"));
const OhioStateUniversity = lazy(() => import("./pages/study-room/OhioStateUniversity"));
const FloridaInternationalUniversity = lazy(() => import("./pages/study-room/FloridaInternationalUniversity"));
const UniversityOfFlorida = lazy(() => import("./pages/study-room/UniversityOfFlorida"));
const NewYorkUniversity = lazy(() => import("./pages/study-room/NewYorkUniversity"));
const UniversityOfTexasAustin = lazy(() => import("./pages/study-room/UniversityOfTexasAustin"));
const ArizonaStateUniversity = lazy(() => import("./pages/study-room/ArizonaStateUniversity"));
const UniversityOfMinnesota = lazy(() => import("./pages/study-room/UniversityOfMinnesota"));
const MichiganStateUniversity = lazy(() => import("./pages/study-room/MichiganStateUniversity"));
const RutgersUniversity = lazy(() => import("./pages/study-room/RutgersUniversity"));
const UniversityOfIllinois = lazy(() => import("./pages/study-room/UniversityOfIllinois"));
const UniversityOfWashington = lazy(() => import("./pages/study-room/UniversityOfWashington"));
const PennStateUniversity = lazy(() => import("./pages/study-room/PennStateUniversity"));
const PurdueUniversity = lazy(() => import("./pages/study-room/PurdueUniversity"));
const UniversityOfSouthernCalifornia = lazy(() => import("./pages/study-room/UniversityOfSouthernCalifornia"));
const IndianaUniversity = lazy(() => import("./pages/study-room/IndianaUniversity"));
const UCLA = lazy(() => import("./pages/study-room/UCLA"));
const UniversityOfMichigan = lazy(() => import("./pages/study-room/UniversityOfMichigan"));
const UCBerkeley = lazy(() => import("./pages/study-room/UCBerkeley"));
const CSWvsQuizlet = lazy(() => import("./pages/quizzes/CSWvsQuizlet"));
const CSWvsTestPrepReview = lazy(() => import("./pages/quizzes/CSWvsTestPrepReview"));
const CSWvsStudioity = lazy(() => import("./pages/quizzes/CSWvsStudioity"));
const CSWvsThea = lazy(() => import("./pages/quizzes/CSWvsThea"));
const CSWvsQuizMe = lazy(() => import("./pages/quizzes/CSWvsQuizMe"));
const CSWvsFlexiQuiz = lazy(() => import("./pages/quizzes/CSWvsFlexiQuiz"));
const CSWvsQuizMaker = lazy(() => import("./pages/quizzes/CSWvsQuizMaker"));
const CSWvsRevisely = lazy(() => import("./pages/quizzes/CSWvsRevisely"));
const CSWvsQuizWhiz = lazy(() => import("./pages/quizzes/CSWvsQuizWhiz"));
const CSWvsTestudy = lazy(() => import("./pages/quizzes/CSWvsTestudy"));
const CSWvsCanva = lazy(() => import("./pages/quizzes/CSWvsCanva"));
const CSWvsGoogleForms = lazy(() => import("./pages/quizzes/CSWvsGoogleForms"));
const CSWvsClassMarker = lazy(() => import("./pages/quizzes/CSWvsClassMarker"));
const CSWvsWooclap = lazy(() => import("./pages/quizzes/CSWvsWooclap"));
const CSWvsGenially = lazy(() => import("./pages/quizzes/CSWvsGenially"));
// Quiz topic pages
const FunQuizzesToTake = lazy(() => import("./pages/quizzes/FunQuizzesToTake"));
const VideoGameQuizzes = lazy(() => import("./pages/quizzes/VideoGameQuizzes"));
const ArtQuizzes = lazy(() => import("./pages/quizzes/ArtQuizzes"));
const MusicQuizzes = lazy(() => import("./pages/quizzes/MusicQuizzes"));
const FoodQuizzes = lazy(() => import("./pages/quizzes/FoodQuizzes"));
const HistoryQuizzes = lazy(() => import("./pages/quizzes/HistoryQuizzes"));
const ScienceQuizzes = lazy(() => import("./pages/quizzes/ScienceQuizzes"));
const PopCultureQuizzes = lazy(() => import("./pages/quizzes/PopCultureQuizzes"));
const TriviaQuizzes = lazy(() => import("./pages/quizzes/TriviaQuizzes"));
const PersonalityQuizzes = lazy(() => import("./pages/quizzes/PersonalityQuizzes"));
// Flashcard comparison pages
const CSWvsNoteGPT = lazy(() => import("./pages/flashcards/CSWvsNoteGPT"));
const CSWvsAnki = lazy(() => import("./pages/flashcards/CSWvsAnki"));
const CSWvsCram = lazy(() => import("./pages/flashcards/CSWvsCram"));
const CSWvsBrainscape = lazy(() => import("./pages/flashcards/CSWvsBrainscape"));
const CSWvsMemrise = lazy(() => import("./pages/flashcards/CSWvsMemrise"));
const CSWvsFlashcardMachine = lazy(() => import("./pages/flashcards/CSWvsFlashcardMachine"));
const CSWvsStudyBlue = lazy(() => import("./pages/flashcards/CSWvsStudyBlue"));
const CSWvsStudyStack = lazy(() => import("./pages/flashcards/CSWvsStudyStack"));
const CSWvsAnkiApp = lazy(() => import("./pages/flashcards/CSWvsAnkiApp"));
const CSWvsKnowt = lazy(() => import("./pages/flashcards/CSWvsKnowt"));
const CSWvsProProfs = lazy(() => import("./pages/flashcards/CSWvsProProfs"));
const CSWvsRemNote = lazy(() => import("./pages/flashcards/CSWvsRemNote"));
const CSWvsKahoot = lazy(() => import("./pages/flashcards/CSWvsKahoot"));
const CSWvsMemozora = lazy(() => import("./pages/flashcards/CSWvsMemozora"));
const CSWvsiDoRecall = lazy(() => import("./pages/flashcards/CSWvsiDoRecall"));
const CSWvsStudyKit = lazy(() => import("./pages/flashcards/CSWvsStudyKit"));
// Workspace comparison pages
const CSWvsNotion = lazy(() => import("./pages/workspace/CSWvsNotion"));
const CSWvsMonday = lazy(() => import("./pages/workspace/CSWvsMonday"));
const CSWvsClickUp = lazy(() => import("./pages/workspace/CSWvsClickUp"));
const CSWvsAsana = lazy(() => import("./pages/workspace/CSWvsAsana"));
const CSWvsTrello = lazy(() => import("./pages/workspace/CSWvsTrello"));
const CSWvsSlack = lazy(() => import("./pages/workspace/CSWvsSlack"));
const CSWvsMiro = lazy(() => import("./pages/workspace/CSWvsMiro"));
const CSWvsBasecamp = lazy(() => import("./pages/workspace/CSWvsBasecamp"));
const CSWvsCoda = lazy(() => import("./pages/workspace/CSWvsCoda"));
const CSWvsAirtable = lazy(() => import("./pages/workspace/CSWvsAirtable"));
// Timer comparison pages
const CSWvsPomodor = lazy(() => import("./pages/timer/CSWvsPomodor"));
const CSWvsOtto = lazy(() => import("./pages/timer/CSWvsOtto"));
const CSWvsForest = lazy(() => import("./pages/timer/CSWvsForest"));
const CSWvsSessionTimer = lazy(() => import("./pages/timer/CSWvsSession"));
const CSWvsFlowTimer = lazy(() => import("./pages/timer/CSWvsFlow"));
const CSWvsTogglTrack = lazy(() => import("./pages/timer/CSWvsTogglTrack"));
const CSWvsFocusBooster = lazy(() => import("./pages/timer/CSWvsFocusBooster"));
const CSWvsBeFocused = lazy(() => import("./pages/timer/CSWvsBeFocused"));
const CSWvsPomoDone = lazy(() => import("./pages/timer/CSWvsPomoDone"));
const CSWvsPomofocus = lazy(() => import("./pages/timer/CSWvsPomofocus"));
const CSWvsFocusKeeper = lazy(() => import("./pages/timer/CSWvsFocusKeeper"));
const CSWvsMinimalist = lazy(() => import("./pages/timer/CSWvsMinimalist"));
const CSWvsFlocus = lazy(() => import("./pages/timer/CSWvsFlocus"));
const CSWvsTaskade = lazy(() => import("./pages/timer/CSWvsTaskade"));
const CSWvsFocusAtWill = lazy(() => import("./pages/timer/CSWvsFocusAtWill"));
const CSWvsColdTurkey = lazy(() => import("./pages/timer/CSWvsColdTurkey"));
const CSWvsBrainFm = lazy(() => import("./pages/timer/CSWvsBrainFm"));
const CSWvsFreedom = lazy(() => import("./pages/timer/CSWvsFreedom"));
const StudyVerse = lazy(() => import("./pages/StudyVerse"));
const StudyVerseRedirect = lazy(() => import("./pages/StudyVerseRedirect"));
const FocusmateAlternative = lazy(() => import("./pages/FocusmateAlternative"));
const StudyWithSKZ = lazy(() => import("./pages/StudyWithSKZ"));
const StudyWithItzy = lazy(() => import("./pages/StudyWithItzy"));
const StudyWithBlackpink = lazy(() => import("./pages/StudyWithBlackpink"));
const StudyWithAespa = lazy(() => import("./pages/StudyWithAespa"));
const StudyWithTwice = lazy(() => import("./pages/StudyWithTwice"));
const StudyWithAteez = lazy(() => import("./pages/StudyWithAteez"));
const StudyWithBTS = lazy(() => import("./pages/StudyWithBTS"));
const StudyWithEnhypen = lazy(() => import("./pages/StudyWithEnhypen"));
const StudyWithNCT = lazy(() => import("./pages/StudyWithNCT"));
const StudyWithTXT = lazy(() => import("./pages/StudyWithTXT"));
const StudyWithSeventeen = lazy(() => import("./pages/StudyWithSeventeen"));
const StudyWithEXO = lazy(() => import("./pages/StudyWithEXO"));
const StudyWithMonstaX = lazy(() => import("./pages/StudyWithMonstaX"));
const StudyWithTheBoyz = lazy(() => import("./pages/StudyWithTheBoyz"));
const StudyWithTreasure = lazy(() => import("./pages/StudyWithTreasure"));
const StudyWithGOT7 = lazy(() => import("./pages/StudyWithGOT7"));
const StudyWithSF9 = lazy(() => import("./pages/StudyWithSF9"));
const StudyWithLoona = lazy(() => import("./pages/StudyWithLoona"));
const StudyWithRedVelvet = lazy(() => import("./pages/StudyWithRedVelvet"));
const StudyWithMamamoo = lazy(() => import("./pages/StudyWithMamamoo"));
const StudyWithKpop = lazy(() => import("./pages/StudyWithKpop"));
const StudyWithMe = lazy(() => import("./pages/StudyWithMe"));
const StudyWithFriends = lazy(() => import("./pages/StudyWithFriends"));
const StudyWithStrangers = lazy(() => import("./pages/StudyWithStrangers"));
const GroupStudy = lazy(() => import("./pages/GroupStudy"));
const ZoomStudyRoom = lazy(() => import("./pages/ZoomStudyRoom"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse"));
const UserRegistrationPolicy = lazy(() => import("./pages/UserRegistrationPolicy"));
const ContentMonitoringPolicy = lazy(() => import("./pages/ContentMonitoringPolicy"));
const ReportPolicy = lazy(() => import("./pages/ReportPolicy"));
const Blog = lazy(() => import("./pages/Blog"));
const HowTurningOnMyCameraBoostedMyProductivity = lazy(() => import("./pages/blog/HowTurningOnMyCameraBoostedMyProductivity"));
const HowToStopProcrastination = lazy(() => import("./pages/blog/HowToStopProcrastination"));
const Top10DigitalToolsForAcademicSuccess = lazy(() => import("./pages/blog/Top10DigitalToolsForAcademicSuccess"));
const PreparingForMajorTest = lazy(() => import("./pages/blog/PreparingForMajorTest"));
const BenefitsOfOnlineLearningCommunity = lazy(() => import("./pages/blog/BenefitsOfOnlineLearningCommunity"));
const ScienceBehindStudyWithMe = lazy(() => import("./pages/blog/ScienceBehindStudyWithMe"));
const AppsBoostProductivityCollege = lazy(() => import("./pages/blog/AppsBoostProductivityCollege"));
const AppsBoostOrganizationCollege = lazy(() => import("./pages/blog/AppsBoostOrganizationCollege"));
const AppsBoostSelfGrowthCollege = lazy(() => import("./pages/blog/AppsBoostSelfGrowthCollege"));
const PaperVsDigitalPlanning = lazy(() => import("./pages/blog/PaperVsDigitalPlanning"));
const TaskManagementToolsReview = lazy(() => import("./pages/blog/TaskManagementToolsReview"));
 const WhyFocusIsHarderThanEver = lazy(() => import("./pages/blog/WhyFocusIsHarderThanEver"));
// Online Study platform pages
const OnlineStudy = lazy(() => import("./pages/OnlineStudy"));
const KhanAcademy = lazy(() => import("./pages/online-study/KhanAcademy"));
const Quizlet = lazy(() => import("./pages/online-study/Quizlet"));
const WolframAlpha = lazy(() => import("./pages/online-study/WolframAlpha"));
const SparkNotes = lazy(() => import("./pages/online-study/SparkNotes"));
const Magoosh = lazy(() => import("./pages/online-study/Magoosh"));
const Kaplan = lazy(() => import("./pages/online-study/Kaplan"));
const PrincetonReview = lazy(() => import("./pages/online-study/PrincetonReview"));
const Fiveable = lazy(() => import("./pages/online-study/Fiveable"));
const Chegg = lazy(() => import("./pages/online-study/Chegg"));
const CourseHero = lazy(() => import("./pages/online-study/CourseHero"));
const Brainly = lazy(() => import("./pages/online-study/Brainly"));
const Focusmate = lazy(() => import("./pages/online-study/Focusmate"));
const FocusmateCn = lazy(() => import("./pages/online-study/FocusmateCn"));
const FocusmateFr = lazy(() => import("./pages/online-study/FocusmateFr"));
const FocusmateJp = lazy(() => import("./pages/online-study/FocusmateJp"));
const FocusmateKr = lazy(() => import("./pages/online-study/FocusmateKr"));
const FocusmateTw = lazy(() => import("./pages/online-study/FocusmateTw"));
const FocusmateVi = lazy(() => import("./pages/online-study/FocusmateVi"));
const StudyStreamPlatform = lazy(() => import("./pages/online-study/StudyStreamPlatform"));
const StudyTogether = lazy(() => import("./pages/online-study/StudyTogether"));
const Coursera = lazy(() => import("./pages/online-study/Coursera"));
const Edx = lazy(() => import("./pages/online-study/Edx"));
const Udemy = lazy(() => import("./pages/online-study/Udemy"));
const YPTYeolPumTa = lazy(() => import("./pages/online-study/YPTYeolPumTa"));
const YPTYeolPumTaKr = lazy(() => import("./pages/online-study/YPTYeolPumTaKr"));
 // Korean localized pages
 const OnlineWorkspaceKr = lazy(() => import("./pages/kr/OnlineWorkspaceKr"));
 const StudyStreamKr = lazy(() => import("./pages/kr/StudyStreamKr"));
 const StudyStreamFr = lazy(() => import("./pages/fr/StudyStreamFr"));
 const StudyStreamJp = lazy(() => import("./pages/jp/StudyStreamJp"));
 const StudyStreamCn = lazy(() => import("./pages/cn/StudyStreamCn"));
 const StudyStreamTw = lazy(() => import("./pages/tw/StudyStreamTw"));
 const StudyStreamVi = lazy(() => import("./pages/vn/StudyStreamVi"));
 const StudyTimerKr = lazy(() => import("./pages/kr/StudyTimerKr"));
 const StudyWithMeKr = lazy(() => import("./pages/kr/StudyWithMeKr"));
 const StudyWithMeCn = lazy(() => import("./pages/cn/StudyWithMeCn"));
 const StudyWithMeTw = lazy(() => import("./pages/tw/StudyWithMeTw"));
 const StudyWithMeJp = lazy(() => import("./pages/jp/StudyWithMeJp"));
 const StudyWithMeFr = lazy(() => import("./pages/fr/StudyWithMeFr"));
 const StudyWithMeVi = lazy(() => import("./pages/vn/StudyWithMeVi"));
 const StudyWithFriendsKr = lazy(() => import("./pages/kr/StudyWithFriendsKr"));
 const StudyWithStrangersKr = lazy(() => import("./pages/kr/StudyWithStrangersKr"));
 const GroupStudyKr = lazy(() => import("./pages/kr/GroupStudyKr"));
const PomodoroTechniqueKr = lazy(() => import("./pages/kr/PomodoroTechniqueKr"));
const PomodoroTechniqueCn = lazy(() => import("./pages/cn/PomodoroTechniqueCn"));
const PomodoroTechniqueFr = lazy(() => import("./pages/fr/PomodoroTechniqueFr"));
const PomodoroTechniqueJp = lazy(() => import("./pages/jp/PomodoroTechniqueJp"));
const PomodoroTechniqueTw = lazy(() => import("./pages/tw/PomodoroTechniqueTw"));
const PomodoroTechniqueVi = lazy(() => import("./pages/vn/PomodoroTechniqueVi"));
const StudyVerseCn = lazy(() => import("./pages/cn/StudyVerseCn"));
const StudyVerseFr = lazy(() => import("./pages/fr/StudyVerseFr"));
const StudyVerseJp = lazy(() => import("./pages/jp/StudyVerseJp"));
const StudyVerseKr = lazy(() => import("./pages/kr/StudyVerseKr"));
const StudyVerseTw = lazy(() => import("./pages/tw/StudyVerseTw"));
const StudyVerseVi = lazy(() => import("./pages/vn/StudyVerseVi"));
const FocusmateAlternativeCn = lazy(() => import("./pages/cn/FocusmateAlternativeCn"));
const FocusmateAlternativeFr = lazy(() => import("./pages/fr/FocusmateAlternativeFr"));
const FocusmateAlternativeJp = lazy(() => import("./pages/jp/FocusmateAlternativeJp"));
const FocusmateAlternativeKr = lazy(() => import("./pages/kr/FocusmateAlternativeKr"));
const FocusmateAlternativeTw = lazy(() => import("./pages/tw/FocusmateAlternativeTw"));
const FocusmateAlternativeVi = lazy(() => import("./pages/vn/FocusmateAlternativeVi"));
const ProductivityToolKr = lazy(() => import("./pages/kr/ProductivityToolKr"));
 // Localized student pages (cn/fr/jp/kr/tw/vn) - StudyRoom / StudyTimer / GroupStudy / StudyWithFriends / StudyWithStrangers
 const StudyRoomCn = lazy(() => import("./pages/cn/StudyRoomCn"));
 const StudyRoomFr = lazy(() => import("./pages/fr/StudyRoomFr"));
 const StudyRoomJp = lazy(() => import("./pages/jp/StudyRoomJp"));
 const StudyRoomKr = lazy(() => import("./pages/kr/StudyRoomKr"));
 const StudyRoomTw = lazy(() => import("./pages/tw/StudyRoomTw"));
 const StudyRoomVi = lazy(() => import("./pages/vn/StudyRoomVi"));
 const StudyTimerCn = lazy(() => import("./pages/cn/StudyTimerCn"));
 const StudyTimerFr = lazy(() => import("./pages/fr/StudyTimerFr"));
 const StudyTimerJp = lazy(() => import("./pages/jp/StudyTimerJp"));
 const StudyTimerTw = lazy(() => import("./pages/tw/StudyTimerTw"));
 const StudyTimerVi = lazy(() => import("./pages/vn/StudyTimerVi"));
 const GroupStudyCn = lazy(() => import("./pages/cn/GroupStudyCn"));
 const GroupStudyFr = lazy(() => import("./pages/fr/GroupStudyFr"));
 const GroupStudyJp = lazy(() => import("./pages/jp/GroupStudyJp"));
 const GroupStudyTw = lazy(() => import("./pages/tw/GroupStudyTw"));
 const GroupStudyVi = lazy(() => import("./pages/vn/GroupStudyVi"));
 const StudyWithFriendsCn = lazy(() => import("./pages/cn/StudyWithFriendsCn"));
 const StudyWithFriendsFr = lazy(() => import("./pages/fr/StudyWithFriendsFr"));
 const StudyWithFriendsJp = lazy(() => import("./pages/jp/StudyWithFriendsJp"));
 const StudyWithFriendsTw = lazy(() => import("./pages/tw/StudyWithFriendsTw"));
 const StudyWithFriendsVi = lazy(() => import("./pages/vn/StudyWithFriendsVi"));
 const StudyWithStrangersCn = lazy(() => import("./pages/cn/StudyWithStrangersCn"));
 const StudyWithStrangersFr = lazy(() => import("./pages/fr/StudyWithStrangersFr"));
 const StudyWithStrangersJp = lazy(() => import("./pages/jp/StudyWithStrangersJp"));
 const StudyWithStrangersTw = lazy(() => import("./pages/tw/StudyWithStrangersTw"));
 const StudyWithStrangersVi = lazy(() => import("./pages/vn/StudyWithStrangersVi"));
const Flown = lazy(() => import("./pages/workspace/Flown"));
const GatherTown = lazy(() => import("./pages/workspace/GatherTown"));
const Caveday = lazy(() => import("./pages/workspace/Caveday"));
const Cofocus = lazy(() => import("./pages/workspace/Cofocus"));
const BlockedPreview = lazy(() => import("./pages/BlockedPreview"));
const StudyVerseHistory = lazy(() => import("./pages/StudyVerseHistory"));
const GrooveHistory = lazy(() => import("./pages/GrooveHistory"));
const Groove = lazy(() => import("./pages/Groove"));
const Edmodo = lazy(() => import("./pages/Edmodo"));
const EdmodoHistory = lazy(() => import("./pages/EdmodoHistory"));
const OpenStudy = lazy(() => import("./pages/OpenStudy"));
const OpenStudyHistory = lazy(() => import("./pages/OpenStudyHistory"));
const Wunderlist = lazy(() => import("./pages/Wunderlist"));
const WunderlistHistory = lazy(() => import("./pages/WunderlistHistory"));
const AltspaceVR = lazy(() => import("./pages/AltspaceVR"));
const AltspaceVRHistory = lazy(() => import("./pages/AltspaceVRHistory"));
const GoogleWave = lazy(() => import("./pages/GoogleWave"));
const GoogleWaveHistory = lazy(() => import("./pages/GoogleWaveHistory"));
const HipChat = lazy(() => import("./pages/HipChat"));
const HipChatHistory = lazy(() => import("./pages/HipChatHistory"));
const Stride = lazy(() => import("./pages/Stride"));
const StrideHistory = lazy(() => import("./pages/StrideHistory"));
const SunriseCalendar = lazy(() => import("./pages/SunriseCalendar"));
const SunriseCalendarHistory = lazy(() => import("./pages/SunriseCalendarHistory"));
const Mailbox = lazy(() => import("./pages/Mailbox"));
const MailboxHistory = lazy(() => import("./pages/MailboxHistory"));
const YahooGroups = lazy(() => import("./pages/YahooGroups"));
const YahooGroupsHistory = lazy(() => import("./pages/YahooGroupsHistory"));
const GoogleSpaces = lazy(() => import("./pages/GoogleSpaces"));
const GoogleSpacesHistory = lazy(() => import("./pages/GoogleSpacesHistory"));
const Quill = lazy(() => import("./pages/Quill"));
const QuillHistory = lazy(() => import("./pages/QuillHistory"));
const GoogleHangouts = lazy(() => import("./pages/GoogleHangouts"));
const GoogleHangoutsHistory = lazy(() => import("./pages/GoogleHangoutsHistory"));
const Chalkup = lazy(() => import("./pages/Chalkup"));
const ChalkupHistory = lazy(() => import("./pages/ChalkupHistory"));
const TodaysMeet = lazy(() => import("./pages/TodaysMeet"));
const TodaysMeetHistory = lazy(() => import("./pages/TodaysMeetHistory"));
const Wikispaces = lazy(() => import("./pages/Wikispaces"));
const WikispacesHistory = lazy(() => import("./pages/WikispacesHistory"));
const Astrid = lazy(() => import("./pages/Astrid"));
const AstridHistory = lazy(() => import("./pages/AstridHistory"));
const Producteev = lazy(() => import("./pages/Producteev"));
const ProducteevHistory = lazy(() => import("./pages/ProducteevHistory"));
const Sandglaz = lazy(() => import("./pages/Sandglaz"));
const SandglazHistory = lazy(() => import("./pages/SandglazHistory"));
const Fetchnotes = lazy(() => import("./pages/Fetchnotes"));
const FetchnotesHistory = lazy(() => import("./pages/FetchnotesHistory"));
const IQTell = lazy(() => import("./pages/IQTell"));
const IQTellHistory = lazy(() => import("./pages/IQTellHistory"));
const Height = lazy(() => import("./pages/Height"));
const HeightHistory = lazy(() => import("./pages/HeightHistory"));
const GoogleTables = lazy(() => import("./pages/GoogleTables"));
const GoogleTablesHistory = lazy(() => import("./pages/GoogleTablesHistory"));
const Alternatives = lazy(() => import("./pages/Alternatives"));
const PitchDeck = lazy(() => import("./pages/PitchDeck"));
const NotFound = lazy(() => import("./pages/NotFound"));
const IndexB2B = lazy(() => import("./pages/IndexB2B"));
const HomePlaceholder = lazy(() => import("./pages/HomePlaceholder"));
const Platform = lazy(() => import("./pages/Platform"));
const Universities = lazy(() => import("./pages/for/Universities"));
const Colleges = lazy(() => import("./pages/for/Colleges"));
const HighSchools = lazy(() => import("./pages/for/HighSchools"));
const ResearchAudience = lazy(() => import("./pages/for/Research"));
const ADHDStudents = lazy(() => import("./pages/for/ADHDStudents"));
const AutismStudents = lazy(() => import("./pages/for/AutismStudents"));
const DyslexiaStudents = lazy(() => import("./pages/for/DyslexiaStudents"));
const DyscalculiaStudents = lazy(() => import("./pages/for/DyscalculiaStudents"));
const DysgraphiaStudents = lazy(() => import("./pages/for/DysgraphiaStudents"));
const TourettesStudents = lazy(() => import("./pages/for/TourettesStudents"));
const UseCasesIndex = lazy(() => import("./pages/use-cases/UseCasesIndex"));
const EarlyWarning = lazy(() => import("./pages/EarlyWarning"));
const Retention = lazy(() => import("./pages/Retention"));
const AdvisingWorkflows = lazy(() => import("./pages/AdvisingWorkflows"));
const EarlyWarningCn = lazy(() => import("./pages/cn/EarlyWarningCn"));
const EarlyWarningFr = lazy(() => import("./pages/fr/EarlyWarningFr"));
const EarlyWarningJp = lazy(() => import("./pages/jp/EarlyWarningJp"));
const EarlyWarningKr = lazy(() => import("./pages/kr/EarlyWarningKr"));
const EarlyWarningTw = lazy(() => import("./pages/tw/EarlyWarningTw"));
const EarlyWarningVi = lazy(() => import("./pages/vn/EarlyWarningVi"));
const RetentionCn = lazy(() => import("./pages/cn/RetentionCn"));
const RetentionFr = lazy(() => import("./pages/fr/RetentionFr"));
const RetentionJp = lazy(() => import("./pages/jp/RetentionJp"));
const RetentionKr = lazy(() => import("./pages/kr/RetentionKr"));
const RetentionTw = lazy(() => import("./pages/tw/RetentionTw"));
const RetentionVi = lazy(() => import("./pages/vn/RetentionVi"));
const AdvisingWorkflowsCn = lazy(() => import("./pages/cn/AdvisingWorkflowsCn"));
const AdvisingWorkflowsFr = lazy(() => import("./pages/fr/AdvisingWorkflowsFr"));
const AdvisingWorkflowsJp = lazy(() => import("./pages/jp/AdvisingWorkflowsJp"));
const AdvisingWorkflowsKr = lazy(() => import("./pages/kr/AdvisingWorkflowsKr"));
const AdvisingWorkflowsTw = lazy(() => import("./pages/tw/AdvisingWorkflowsTw"));
const AdvisingWorkflowsVi = lazy(() => import("./pages/vn/AdvisingWorkflowsVi"));
const UniversitiesCn = lazy(() => import("./pages/cn/UniversitiesCn"));
const UniversitiesFr = lazy(() => import("./pages/fr/UniversitiesFr"));
const UniversitiesJp = lazy(() => import("./pages/jp/UniversitiesJp"));
const UniversitiesKr = lazy(() => import("./pages/kr/UniversitiesKr"));
const UniversitiesTw = lazy(() => import("./pages/tw/UniversitiesTw"));
const UniversitiesVi = lazy(() => import("./pages/vn/UniversitiesVi"));
const CollegesCn = lazy(() => import("./pages/cn/CollegesCn"));
const CollegesFr = lazy(() => import("./pages/fr/CollegesFr"));
const CollegesJp = lazy(() => import("./pages/jp/CollegesJp"));
const CollegesKr = lazy(() => import("./pages/kr/CollegesKr"));
const CollegesTw = lazy(() => import("./pages/tw/CollegesTw"));
const CollegesVi = lazy(() => import("./pages/vn/CollegesVi"));
const HighSchoolsCn = lazy(() => import("./pages/cn/HighSchoolsCn"));
const HighSchoolsFr = lazy(() => import("./pages/fr/HighSchoolsFr"));
const HighSchoolsJp = lazy(() => import("./pages/jp/HighSchoolsJp"));
const HighSchoolsKr = lazy(() => import("./pages/kr/HighSchoolsKr"));
const HighSchoolsTw = lazy(() => import("./pages/tw/HighSchoolsTw"));
const HighSchoolsVi = lazy(() => import("./pages/vn/HighSchoolsVi"));
const ADHDStudentsCn = lazy(() => import("./pages/cn/ADHDStudentsCn"));
const ADHDStudentsFr = lazy(() => import("./pages/fr/ADHDStudentsFr"));
const ADHDStudentsJp = lazy(() => import("./pages/jp/ADHDStudentsJp"));
const ADHDStudentsKr = lazy(() => import("./pages/kr/ADHDStudentsKr"));
const ADHDStudentsTw = lazy(() => import("./pages/tw/ADHDStudentsTw"));
const ADHDStudentsVi = lazy(() => import("./pages/vn/ADHDStudentsVi"));
const InstitutionCn = lazy(() => import("./pages/cn/InstitutionCn"));
const InstitutionFr = lazy(() => import("./pages/fr/InstitutionFr"));
const InstitutionJp = lazy(() => import("./pages/jp/InstitutionJp"));
const InstitutionKr = lazy(() => import("./pages/kr/InstitutionKr"));
const InstitutionTw = lazy(() => import("./pages/tw/InstitutionTw"));
const InstitutionVi = lazy(() => import("./pages/vn/InstitutionVi"));
const PlatformCn = lazy(() => import("./pages/cn/PlatformCn"));
const PlatformFr = lazy(() => import("./pages/fr/PlatformFr"));
const PlatformJp = lazy(() => import("./pages/jp/PlatformJp"));
const PlatformKr = lazy(() => import("./pages/kr/PlatformKr"));
const PlatformTw = lazy(() => import("./pages/tw/PlatformTw"));
const PlatformVi = lazy(() => import("./pages/vn/PlatformVi"));
const SensoryFriendlyStudy = lazy(() => import("./pages/SensoryFriendlyStudy"));
const StudyRoutines = lazy(() => import("./pages/StudyRoutines"));
const AutisticStudents = lazy(() => import("./pages/AutisticStudents"));
const TroubleFocusing = lazy(() => import("./pages/TroubleFocusing"));
const TroubleStartingTasks = lazy(() => import("./pages/TroubleStartingTasks"));
const Demo = lazy(() => import("./pages/Demo"));
const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <CountryBlock>
        <ScrollToTop />
        <Toaster />
        <Sonner />
        <Suspense fallback={null}><Routes>
          <Route path="/" element={<Index />} />
          <Route path="/students/" element={<Students />} />
          <Route path="/cn/students/" element={<StudentsCn />} />
          <Route path="/fr/students/" element={<StudentsFr />} />
          <Route path="/jp/students/" element={<StudentsJp />} />
          <Route path="/kr/students/" element={<StudentsKr />} />
          <Route path="/tw/students/" element={<StudentsTw />} />
          <Route path="/vn/students/" element={<StudentsVi />} />
          <Route path="/institution/" element={<IndexB2B />} />
          <Route path="/cn/institution/" element={<InstitutionCn />} />
          <Route path="/fr/institution/" element={<InstitutionFr />} />
          <Route path="/jp/institution/" element={<InstitutionJp />} />
          <Route path="/kr/institution/" element={<InstitutionKr />} />
          <Route path="/tw/institution/" element={<InstitutionTw />} />
          <Route path="/vn/institution/" element={<InstitutionVi />} />

          <Route path="/platform/" element={<Platform />} />
          <Route path="/cn/platform/" element={<PlatformCn />} />
          <Route path="/fr/platform/" element={<PlatformFr />} />
          <Route path="/jp/platform/" element={<PlatformJp />} />
          <Route path="/kr/platform/" element={<PlatformKr />} />
          <Route path="/tw/platform/" element={<PlatformTw />} />
          <Route path="/vn/platform/" element={<PlatformVi />} />
          <Route path="/universities/" element={<Universities />} />
          <Route path="/colleges/" element={<Colleges />} />
          <Route path="/high-schools/" element={<HighSchools />} />
          <Route path="/research/" element={<ResearchAudience />} />
          <Route path="/adhd-students/" element={<ADHDStudents />} />
          <Route path="/autism-students/" element={<AutismStudents />} />
          <Route path="/sensory-friendly-study/" element={<SensoryFriendlyStudy />} />
          <Route path="/study-routines/" element={<StudyRoutines />} />
          <Route path="/autistic-students/" element={<AutisticStudents />} />
          <Route path="/trouble-focusing/" element={<TroubleFocusing />} />
          <Route path="/trouble-starting-tasks/" element={<TroubleStartingTasks />} />
          <Route path="/dyslexia-students/" element={<DyslexiaStudents />} />
          <Route path="/dyscalculia-students/" element={<DyscalculiaStudents />} />
          <Route path="/dysgraphia-students/" element={<DysgraphiaStudents />} />
          <Route path="/tourettes-students/" element={<TourettesStudents />} />
          <Route path="/use-cases/" element={<UseCasesIndex />} />
          <Route path="/early-warning/" element={<EarlyWarning />} />
          <Route path="/retention/" element={<Retention />} />
          <Route path="/use-cases/early-warning/" element={<Navigate to="/early-warning/" replace />} />
          <Route path="/use-cases/retention/" element={<Navigate to="/retention/" replace />} />
          <Route path="/advising-workflows/" element={<AdvisingWorkflows />} />
          <Route path="/use-cases/advising-workflows/" element={<Navigate to="/advising-workflows/" replace />} />
          <Route path="/cn/early-warning/" element={<EarlyWarningCn />} />
          <Route path="/fr/early-warning/" element={<EarlyWarningFr />} />
          <Route path="/jp/early-warning/" element={<EarlyWarningJp />} />
          <Route path="/kr/early-warning/" element={<EarlyWarningKr />} />
          <Route path="/tw/early-warning/" element={<EarlyWarningTw />} />
          <Route path="/vn/early-warning/" element={<EarlyWarningVi />} />
          <Route path="/cn/retention/" element={<RetentionCn />} />
          <Route path="/fr/retention/" element={<RetentionFr />} />
          <Route path="/jp/retention/" element={<RetentionJp />} />
          <Route path="/kr/retention/" element={<RetentionKr />} />
          <Route path="/tw/retention/" element={<RetentionTw />} />
          <Route path="/vn/retention/" element={<RetentionVi />} />
          <Route path="/cn/advising-workflows/" element={<AdvisingWorkflowsCn />} />
          <Route path="/fr/advising-workflows/" element={<AdvisingWorkflowsFr />} />
          <Route path="/jp/advising-workflows/" element={<AdvisingWorkflowsJp />} />
          <Route path="/kr/advising-workflows/" element={<AdvisingWorkflowsKr />} />
          <Route path="/tw/advising-workflows/" element={<AdvisingWorkflowsTw />} />
          <Route path="/vn/advising-workflows/" element={<AdvisingWorkflowsVi />} />
          <Route path="/cn/universities/" element={<UniversitiesCn />} />
          <Route path="/fr/universities/" element={<UniversitiesFr />} />
          <Route path="/jp/universities/" element={<UniversitiesJp />} />
          <Route path="/kr/universities/" element={<UniversitiesKr />} />
          <Route path="/tw/universities/" element={<UniversitiesTw />} />
          <Route path="/vn/universities/" element={<UniversitiesVi />} />
          <Route path="/cn/colleges/" element={<CollegesCn />} />
          <Route path="/fr/colleges/" element={<CollegesFr />} />
          <Route path="/jp/colleges/" element={<CollegesJp />} />
          <Route path="/kr/colleges/" element={<CollegesKr />} />
          <Route path="/tw/colleges/" element={<CollegesTw />} />
          <Route path="/vn/colleges/" element={<CollegesVi />} />
          <Route path="/cn/high-schools/" element={<HighSchoolsCn />} />
          <Route path="/fr/high-schools/" element={<HighSchoolsFr />} />
          <Route path="/jp/high-schools/" element={<HighSchoolsJp />} />
          <Route path="/kr/high-schools/" element={<HighSchoolsKr />} />
          <Route path="/tw/high-schools/" element={<HighSchoolsTw />} />
          <Route path="/vn/high-schools/" element={<HighSchoolsVi />} />
          <Route path="/cn/adhd-students/" element={<ADHDStudentsCn />} />
          <Route path="/fr/adhd-students/" element={<ADHDStudentsFr />} />
          <Route path="/jp/adhd-students/" element={<ADHDStudentsJp />} />
          <Route path="/kr/adhd-students/" element={<ADHDStudentsKr />} />
          <Route path="/tw/adhd-students/" element={<ADHDStudentsTw />} />
          <Route path="/vn/adhd-students/" element={<ADHDStudentsVi />} />
          <Route path="/demo/" element={<Demo />} />
          <Route path="/redesign-preview/" element={<RedesignPreview />} />
          <Route path="/home-canada-preview/" element={<HomeCanadaPreview />} />
          <Route path="/home-canada-preview" element={<HomeCanadaPreview />} />
          <Route path="/pitch/" element={<PitchDeck />} />
        <Route path="/fr/" element={<IndexFr />} />
        <Route path="/vn/" element={<IndexVi />} />
        <Route path="/tw/" element={<IndexTw />} />
        <Route path="/kr/" element={<IndexKr />} />
        <Route path="/jp/" element={<IndexJp />} />
        <Route path="/cn/" element={<IndexCn />} />
        <Route path="/kr/online-study/ypt-yeolpumta/" element={<YPTYeolPumTaKr />} />
         <Route path="/kr/online-workspace/" element={<OnlineWorkspaceKr />} />
         <Route path="/kr/study-stream/" element={<StudyStreamKr />} />
         <Route path="/fr/study-stream/" element={<StudyStreamFr />} />
         <Route path="/jp/study-stream/" element={<StudyStreamJp />} />
         <Route path="/cn/study-stream/" element={<StudyStreamCn />} />
         <Route path="/tw/study-stream/" element={<StudyStreamTw />} />
         <Route path="/vn/study-stream/" element={<StudyStreamVi />} />
         <Route path="/kr/study-timer/" element={<StudyTimerKr />} />
         <Route path="/kr/study-with-me/" element={<StudyWithMeKr />} />
         <Route path="/cn/study-with-me/" element={<StudyWithMeCn />} />
         <Route path="/tw/study-with-me/" element={<StudyWithMeTw />} />
         <Route path="/jp/study-with-me/" element={<StudyWithMeJp />} />
         <Route path="/fr/study-with-me/" element={<StudyWithMeFr />} />
         <Route path="/vn/study-with-me/" element={<StudyWithMeVi />} />
         <Route path="/kr/study-with-friends/" element={<StudyWithFriendsKr />} />
         <Route path="/kr/study-with-strangers/" element={<StudyWithStrangersKr />} />
         <Route path="/kr/group-study/" element={<GroupStudyKr />} />
        <Route path="/kr/pomodoro-technique/" element={<PomodoroTechniqueKr />} />
        <Route path="/cn/pomodoro-technique/" element={<PomodoroTechniqueCn />} />
        <Route path="/fr/pomodoro-technique/" element={<PomodoroTechniqueFr />} />
        <Route path="/jp/pomodoro-technique/" element={<PomodoroTechniqueJp />} />
        <Route path="/tw/pomodoro-technique/" element={<PomodoroTechniqueTw />} />
        <Route path="/vn/pomodoro-technique/" element={<PomodoroTechniqueVi />} />
        <Route path="/cn/studyverse/" element={<StudyVerseCn />} />
        <Route path="/fr/studyverse/" element={<StudyVerseFr />} />
        <Route path="/jp/studyverse/" element={<StudyVerseJp />} />
        <Route path="/kr/studyverse/" element={<StudyVerseKr />} />
        <Route path="/tw/studyverse/" element={<StudyVerseTw />} />
        <Route path="/vn/studyverse/" element={<StudyVerseVi />} />
        <Route path="/cn/focusmate/" element={<FocusmateAlternativeCn />} />
        <Route path="/fr/focusmate/" element={<FocusmateAlternativeFr />} />
        <Route path="/jp/focusmate/" element={<FocusmateAlternativeJp />} />
        <Route path="/kr/focusmate/" element={<FocusmateAlternativeKr />} />
        <Route path="/tw/focusmate/" element={<FocusmateAlternativeTw />} />
        <Route path="/vn/focusmate/" element={<FocusmateAlternativeVi />} />
         <Route path="/kr/productivity-tool/" element={<ProductivityToolKr />} />
         {/* Localized student pages (6 langs × 5 pages = 30 routes; 5 study-with-me already declared above) */}
         <Route path="/cn/study-room/" element={<StudyRoomCn />} />
         <Route path="/fr/study-room/" element={<StudyRoomFr />} />
         <Route path="/jp/study-room/" element={<StudyRoomJp />} />
         <Route path="/kr/study-room/" element={<StudyRoomKr />} />
         <Route path="/tw/study-room/" element={<StudyRoomTw />} />
         <Route path="/vn/study-room/" element={<StudyRoomVi />} />
         <Route path="/cn/study-timer/" element={<StudyTimerCn />} />
         <Route path="/fr/study-timer/" element={<StudyTimerFr />} />
         <Route path="/jp/study-timer/" element={<StudyTimerJp />} />
         <Route path="/tw/study-timer/" element={<StudyTimerTw />} />
         <Route path="/vn/study-timer/" element={<StudyTimerVi />} />
         <Route path="/cn/group-study/" element={<GroupStudyCn />} />
         <Route path="/fr/group-study/" element={<GroupStudyFr />} />
         <Route path="/jp/group-study/" element={<GroupStudyJp />} />
         <Route path="/tw/group-study/" element={<GroupStudyTw />} />
         <Route path="/vn/group-study/" element={<GroupStudyVi />} />
         <Route path="/cn/study-with-friends/" element={<StudyWithFriendsCn />} />
         <Route path="/fr/study-with-friends/" element={<StudyWithFriendsFr />} />
         <Route path="/jp/study-with-friends/" element={<StudyWithFriendsJp />} />
         <Route path="/tw/study-with-friends/" element={<StudyWithFriendsTw />} />
         <Route path="/vn/study-with-friends/" element={<StudyWithFriendsVi />} />
         <Route path="/cn/study-with-strangers/" element={<StudyWithStrangersCn />} />
         <Route path="/fr/study-with-strangers/" element={<StudyWithStrangersFr />} />
         <Route path="/jp/study-with-strangers/" element={<StudyWithStrangersJp />} />
         <Route path="/tw/study-with-strangers/" element={<StudyWithStrangersTw />} />
         <Route path="/vn/study-with-strangers/" element={<StudyWithStrangersVi />} />
        <Route path="/online-workspace/" element={<OnlineWorkspace />} />
        <Route path="/online-workspace/csw-vs-notion/" element={<CSWvsNotion />} />
        <Route path="/online-workspace/csw-vs-monday/" element={<CSWvsMonday />} />
        <Route path="/online-workspace/csw-vs-clickup/" element={<CSWvsClickUp />} />
        <Route path="/online-workspace/csw-vs-asana/" element={<CSWvsAsana />} />
        <Route path="/online-workspace/csw-vs-trello/" element={<CSWvsTrello />} />
        <Route path="/online-workspace/csw-vs-slack/" element={<CSWvsSlack />} />
        <Route path="/online-workspace/csw-vs-miro/" element={<CSWvsMiro />} />
        <Route path="/online-workspace/csw-vs-basecamp/" element={<CSWvsBasecamp />} />
        <Route path="/online-workspace/csw-vs-coda/" element={<CSWvsCoda />} />
        <Route path="/online-workspace/csw-vs-airtable/" element={<CSWvsAirtable />} />
        <Route path="/productivity-tool/" element={<ProductivityTool />} />
        <Route path="/study-stream/" element={<StudyStreamPage />} />
        <Route path="/study-stream/why-studying-on-camera-helps/" element={<WhyStudyingOnCameraHelps />} />
        <Route path="/study-stream/gongbang-to-global/" element={<GongbangToGlobal />} />
        <Route path="/study-stream/tech-toolkit-for-success/" element={<TechToolkitSuccess />} />
        <Route path="/study-stream/productivity-hacks/" element={<ProductivityHacks />} />
        <Route path="/study-stream/study-stream-vs-study-group/" element={<StudyStreamVsStudyGroup />} />
        <Route path="/study-stream/studying-alone-together/" element={<StudyingAloneTogether />} />
        <Route path="/study-stream/pomodoro-20/" element={<Pomodoro20 />} />
        <Route path="/pomodoro-technique/" element={<PomodoroTechnique />} />
        <Route path="/pomodoro-technique/pomodoro-101/" element={<Pomodoro101 />} />
        <Route path="/pomodoro-technique/science-of-focus/" element={<ScienceOfFocus />} />
        <Route path="/pomodoro-technique/does-it-work/" element={<DoesItWork />} />
        <Route path="/pomodoro-technique/pomodoro-vs-procrastination/" element={<PomodoroVsProcrastination />} />
        <Route path="/pomodoro-technique/study-habits/" element={<StudyHabits />} />
        <Route path="/pomodoro-technique/avoid-burnout/" element={<AvoidBurnout />} />
        <Route path="/pomodoro-technique/best-tools/" element={<BestTools />} />
        <Route path="/pomodoro-technique/study-streams/" element={<StudyStreams />} />
        <Route path="/pomodoro-technique/customize/" element={<CustomizePomodoro />} />
        <Route path="/pomodoro-technique/why-breaks-matter/" element={<WhyBreaksMatter />} />
        <Route path="/pomodoro-technique/exam-prep/" element={<ExamPrep />} />
        <Route path="/pomodoro-technique/active-learning/" element={<ActiveLearning />} />
        <Route path="/pomodoro-technique/adhd/" element={<PomodoroADHD />} />
        <Route path="/studystream/" element={<StudyStreamRedirect />} />
        <Route path="/studystream" element={<StudyStreamRedirect />} />
        <Route path="/reminders/" element={<Reminders />} />
        <Route path="/study-timer/" element={<StudyTimer />} />
        <Route path="/study-timer/csw-vs-pomodor/" element={<CSWvsPomodor />} />
        <Route path="/study-timer/csw-vs-otto/" element={<CSWvsOtto />} />
        <Route path="/study-timer/csw-vs-forest/" element={<CSWvsForest />} />
        <Route path="/study-timer/csw-vs-session/" element={<CSWvsSessionTimer />} />
        <Route path="/study-timer/csw-vs-flow/" element={<CSWvsFlowTimer />} />
        <Route path="/study-timer/csw-vs-toggl-track/" element={<CSWvsTogglTrack />} />
        <Route path="/study-timer/csw-vs-focus-booster/" element={<CSWvsFocusBooster />} />
        <Route path="/study-timer/csw-vs-be-focused/" element={<CSWvsBeFocused />} />
        <Route path="/study-timer/csw-vs-pomodone/" element={<CSWvsPomoDone />} />
        <Route path="/study-timer/csw-vs-pomofocus/" element={<CSWvsPomofocus />} />
        <Route path="/study-timer/csw-vs-focus-keeper/" element={<CSWvsFocusKeeper />} />
        <Route path="/study-timer/csw-vs-minimalist/" element={<CSWvsMinimalist />} />
        <Route path="/study-timer/csw-vs-flocus/" element={<CSWvsFlocus />} />
        <Route path="/study-timer/csw-vs-taskade/" element={<CSWvsTaskade />} />
        <Route path="/study-timer/csw-vs-focus-at-will/" element={<CSWvsFocusAtWill />} />
        <Route path="/study-timer/csw-vs-cold-turkey/" element={<CSWvsColdTurkey />} />
        <Route path="/study-timer/csw-vs-brain-fm/" element={<CSWvsBrainFm />} />
        <Route path="/study-timer/csw-vs-freedom/" element={<CSWvsFreedom />} />
        <Route path="/task-management/" element={<TaskManagement />} />
        <Route path="/tasks/" element={<TasksRedirect />} />
        <Route path="/tasks" element={<TasksRedirect />} />
        <Route path="/quizzes/" element={<Quizzes />} />
        <Route path="/quizzes/csw-vs-quizlet/" element={<CSWvsQuizlet />} />
        <Route path="/quizzes/csw-vs-test-prep-review/" element={<CSWvsTestPrepReview />} />
        <Route path="/quizzes/csw-vs-studiosity/" element={<CSWvsStudioity />} />
        <Route path="/quizzes/csw-vs-thea/" element={<CSWvsThea />} />
        <Route path="/quizzes/csw-vs-quiz-me/" element={<CSWvsQuizMe />} />
        <Route path="/quizzes/csw-vs-flexiquiz/" element={<CSWvsFlexiQuiz />} />
        <Route path="/quizzes/csw-vs-quiz-maker/" element={<CSWvsQuizMaker />} />
        <Route path="/quizzes/csw-vs-revisely/" element={<CSWvsRevisely />} />
        <Route path="/quizzes/csw-vs-quizwhiz/" element={<CSWvsQuizWhiz />} />
        <Route path="/quizzes/csw-vs-testudy/" element={<CSWvsTestudy />} />
        <Route path="/quizzes/csw-vs-canva/" element={<CSWvsCanva />} />
        <Route path="/quizzes/csw-vs-google-forms/" element={<CSWvsGoogleForms />} />
        <Route path="/quizzes/csw-vs-classmarker/" element={<CSWvsClassMarker />} />
        <Route path="/quizzes/csw-vs-wooclap/" element={<CSWvsWooclap />} />
        <Route path="/quizzes/csw-vs-genially/" element={<CSWvsGenially />} />
        <Route path="/quizzes/fun-quizzes-to-take/" element={<FunQuizzesToTake />} />
        <Route path="/quizzes/video-game-quizzes/" element={<VideoGameQuizzes />} />
        <Route path="/quizzes/art-quizzes/" element={<ArtQuizzes />} />
        <Route path="/quizzes/music-quizzes/" element={<MusicQuizzes />} />
        <Route path="/quizzes/food-quizzes/" element={<FoodQuizzes />} />
        <Route path="/quizzes/history-quizzes/" element={<HistoryQuizzes />} />
        <Route path="/quizzes/science-quizzes/" element={<ScienceQuizzes />} />
        <Route path="/quizzes/pop-culture-quizzes/" element={<PopCultureQuizzes />} />
        <Route path="/quizzes/trivia-quizzes/" element={<TriviaQuizzes />} />
        <Route path="/quizzes/personality-quizzes/" element={<PersonalityQuizzes />} />
        <Route path="/flash-cards/" element={<FlashCards />} />
        <Route path="/flash-cards/csw-vs-notegpt/" element={<CSWvsNoteGPT />} />
        <Route path="/flash-cards/csw-vs-anki/" element={<CSWvsAnki />} />
        <Route path="/flash-cards/csw-vs-cram/" element={<CSWvsCram />} />
        <Route path="/flash-cards/csw-vs-brainscape/" element={<CSWvsBrainscape />} />
        <Route path="/flash-cards/csw-vs-memrise/" element={<CSWvsMemrise />} />
        <Route path="/flash-cards/csw-vs-flashcard-machine/" element={<CSWvsFlashcardMachine />} />
        <Route path="/flash-cards/csw-vs-studyblue/" element={<CSWvsStudyBlue />} />
        <Route path="/flash-cards/csw-vs-studystack/" element={<CSWvsStudyStack />} />
        <Route path="/flash-cards/csw-vs-ankiapp/" element={<CSWvsAnkiApp />} />
        <Route path="/flash-cards/csw-vs-knowt/" element={<CSWvsKnowt />} />
        <Route path="/flash-cards/csw-vs-proprofs/" element={<CSWvsProProfs />} />
        <Route path="/flash-cards/csw-vs-remnote/" element={<CSWvsRemNote />} />
        <Route path="/flash-cards/csw-vs-kahoot/" element={<CSWvsKahoot />} />
        <Route path="/flash-cards/csw-vs-memozora/" element={<CSWvsMemozora />} />
        <Route path="/flash-cards/csw-vs-idorecall/" element={<CSWvsiDoRecall />} />
        <Route path="/flash-cards/csw-vs-studykit/" element={<CSWvsStudyKit />} />
        <Route path="/study-room/" element={<StudyRoom />} />
        {/* Toronto Area Universities */}
        <Route path="/study-room/university-of-toronto-st-george/" element={<UofTStGeorge />} />
        <Route path="/study-room/university-of-toronto-mississauga/" element={<UofTMississauga />} />
        <Route path="/study-room/university-of-toronto-scarborough/" element={<UofTScarborough />} />
        <Route path="/study-room/york-university/" element={<YorkUniversity />} />
        <Route path="/study-room/toronto-metropolitan-university/" element={<TorontoMetropolitanUniversity />} />
        <Route path="/study-room/ocad-university/" element={<OCADUniversity />} />
        <Route path="/study-room/university-of-guelph-humber/" element={<GuelphHumber />} />
        <Route path="/study-room/seneca-college/" element={<SenecaCollege />} />
        <Route path="/study-room/humber-college/" element={<HumberCollege />} />
        {/* Canadian Universities */}
        <Route path="/study-room/mcgill-university/" element={<McGillUniversity />} />
        <Route path="/study-room/ubc-vancouver/" element={<UBCVancouver />} />
        <Route path="/study-room/university-of-alberta/" element={<UniversityOfAlberta />} />
        <Route path="/study-room/university-of-calgary/" element={<UniversityOfCalgary />} />
        <Route path="/study-room/university-of-ottawa/" element={<UniversityOfOttawa />} />
        <Route path="/study-room/mcmaster-university/" element={<McMasterUniversity />} />
        <Route path="/study-room/university-of-waterloo/" element={<UniversityOfWaterloo />} />
        <Route path="/study-room/western-university/" element={<WesternUniversity />} />
        <Route path="/study-room/queens-university/" element={<QueensUniversity />} />
        <Route path="/study-room/university-of-manitoba/" element={<UniversityOfManitoba />} />
        <Route path="/study-room/university-of-saskatchewan/" element={<UniversityOfSaskatchewan />} />
        <Route path="/study-room/universite-laval/" element={<UniversiteLaval />} />
        <Route path="/study-room/universite-de-montreal/" element={<UniversiteDeMontreal />} />
        <Route path="/study-room/dalhousie-university/" element={<DalhousieUniversity />} />
        <Route path="/study-room/simon-fraser-university/" element={<SimonFraserUniversity />} />
        <Route path="/study-room/concordia-university/" element={<ConcordiaUniversity />} />
        <Route path="/study-room/university-of-victoria/" element={<UniversityOfVictoria />} />
        <Route path="/study-room/memorial-university/" element={<MemorialUniversity />} />
        <Route path="/study-room/carleton-university/" element={<CarletonUniversity />} />
        <Route path="/study-room/university-of-new-brunswick/" element={<UniversityOfNewBrunswick />} />
        {/* US Universities */}
        <Route path="/study-room/university-of-central-florida/" element={<UniversityOfCentralFlorida />} />
        <Route path="/study-room/texas-am-university/" element={<TexasAMUniversity />} />
        <Route path="/study-room/ohio-state-university/" element={<OhioStateUniversity />} />
        <Route path="/study-room/florida-international-university/" element={<FloridaInternationalUniversity />} />
        <Route path="/study-room/university-of-florida/" element={<UniversityOfFlorida />} />
        <Route path="/study-room/new-york-university/" element={<NewYorkUniversity />} />
        <Route path="/study-room/university-of-texas-austin/" element={<UniversityOfTexasAustin />} />
        <Route path="/study-room/arizona-state-university/" element={<ArizonaStateUniversity />} />
        <Route path="/study-room/university-of-minnesota/" element={<UniversityOfMinnesota />} />
        <Route path="/study-room/michigan-state-university/" element={<MichiganStateUniversity />} />
        <Route path="/study-room/rutgers-university/" element={<RutgersUniversity />} />
        <Route path="/study-room/university-of-illinois/" element={<UniversityOfIllinois />} />
        <Route path="/study-room/university-of-washington/" element={<UniversityOfWashington />} />
        <Route path="/study-room/penn-state-university/" element={<PennStateUniversity />} />
        <Route path="/study-room/purdue-university/" element={<PurdueUniversity />} />
        <Route path="/study-room/university-of-southern-california/" element={<UniversityOfSouthernCalifornia />} />
        <Route path="/study-room/indiana-university/" element={<IndianaUniversity />} />
        <Route path="/study-room/ucla/" element={<UCLA />} />
        <Route path="/study-room/university-of-michigan/" element={<UniversityOfMichigan />} />
        <Route path="/study-room/uc-berkeley/" element={<UCBerkeley />} />
        <Route path="/studyverse/" element={<StudyVerse />} />
        <Route path="/studyverse/history/" element={<StudyVerseHistory />} />
        <Route path="/study-verse/" element={<StudyVerseRedirect />} />
        <Route path="/study-verse" element={<StudyVerseRedirect />} />
        <Route path="/focusmate/" element={<FocusmateAlternative />} />
        <Route path="/focusmate" element={<FocusmateAlternative />} />
        <Route path="/groove/" element={<Groove />} />
        <Route path="/groove/history/" element={<GrooveHistory />} />
        <Route path="/edmodo/" element={<Edmodo />} />
        <Route path="/edmodo/history/" element={<EdmodoHistory />} />
        <Route path="/study-with-skz/" element={<StudyWithSKZ />} />
        <Route path="/study-with-itzy/" element={<StudyWithItzy />} />
        <Route path="/study-with-blackpink/" element={<StudyWithBlackpink />} />
        <Route path="/study-with-aespa/" element={<StudyWithAespa />} />
        <Route path="/study-with-twice/" element={<StudyWithTwice />} />
        <Route path="/study-with-ateez/" element={<StudyWithAteez />} />
        <Route path="/study-with-bts/" element={<StudyWithBTS />} />
        <Route path="/study-with-enhypen/" element={<StudyWithEnhypen />} />
        <Route path="/study-with-nct/" element={<StudyWithNCT />} />
        <Route path="/study-with-txt/" element={<StudyWithTXT />} />
        <Route path="/study-with-seventeen/" element={<StudyWithSeventeen />} />
        <Route path="/study-with-exo/" element={<StudyWithEXO />} />
        <Route path="/study-with-monsta-x/" element={<StudyWithMonstaX />} />
        <Route path="/study-with-the-boyz/" element={<StudyWithTheBoyz />} />
        <Route path="/study-with-treasure/" element={<StudyWithTreasure />} />
        <Route path="/study-with-got7/" element={<StudyWithGOT7 />} />
        <Route path="/study-with-sf9/" element={<StudyWithSF9 />} />
        <Route path="/study-with-loona/" element={<StudyWithLoona />} />
        <Route path="/study-with-red-velvet/" element={<StudyWithRedVelvet />} />
        <Route path="/study-with-mamamoo/" element={<StudyWithMamamoo />} />
        <Route path="/study-with-kpop/" element={<StudyWithKpop />} />
        <Route path="/study-with-me/" element={<StudyWithMe />} />
        <Route path="/study-with-friends/" element={<StudyWithFriends />} />
        <Route path="/study-with-strangers/" element={<StudyWithStrangers />} />
        <Route path="/group-study/" element={<GroupStudy />} />
        <Route path="/zoom-study-room/" element={<ZoomStudyRoom />} />
        <Route path="/privacy-policy/" element={<PrivacyPolicy />} />
        <Route path="/terms-of-use/" element={<TermsOfUse />} />
        <Route path="/registration-policy/" element={<UserRegistrationPolicy />} />
        <Route path="/content-monitoring-policy/" element={<ContentMonitoringPolicy />} />
        <Route path="/report-policy/" element={<ReportPolicy />} />
        <Route path="/blog/" element={<Blog />} />
        <Route path="/blog/how-turning-on-my-camera-boosted-my-productivity/" element={<HowTurningOnMyCameraBoostedMyProductivity />} />
        <Route path="/blog/how-to-stop-procrastination/" element={<HowToStopProcrastination />} />
        <Route path="/blog/top-10-digital-tools-for-academic-success/" element={<Top10DigitalToolsForAcademicSuccess />} />
        <Route path="/blog/preparing-for-major-test/" element={<PreparingForMajorTest />} />
        <Route path="/blog/benefits-of-online-learning-community/" element={<BenefitsOfOnlineLearningCommunity />} />
        <Route path="/blog/science-behind-study-with-me/" element={<ScienceBehindStudyWithMe />} />
        <Route path="/blog/apps-boost-productivity-college/" element={<AppsBoostProductivityCollege />} />
        <Route path="/blog/apps-boost-organization-college/" element={<AppsBoostOrganizationCollege />} />
        <Route path="/blog/apps-boost-self-growth-college/" element={<AppsBoostSelfGrowthCollege />} />
        <Route path="/blog/paper-vs-digital-planning/" element={<PaperVsDigitalPlanning />} />
        <Route path="/blog/task-management-tools-review/" element={<TaskManagementToolsReview />} />
         <Route path="/blog/why-focus-is-harder-than-ever/" element={<WhyFocusIsHarderThanEver />} />
        {/* Online Study routes */}
        <Route path="/online-study/" element={<OnlineStudy />} />
        <Route path="/online-study/khan-academy/" element={<KhanAcademy />} />
        <Route path="/online-study/quizlet/" element={<Quizlet />} />
        <Route path="/online-study/wolfram-alpha/" element={<WolframAlpha />} />
        <Route path="/online-study/sparknotes/" element={<SparkNotes />} />
        <Route path="/online-study/magoosh/" element={<Magoosh />} />
        <Route path="/online-study/kaplan/" element={<Kaplan />} />
        <Route path="/online-study/princeton-review/" element={<PrincetonReview />} />
        <Route path="/online-study/fiveable/" element={<Fiveable />} />
        <Route path="/online-study/chegg/" element={<Chegg />} />
        <Route path="/online-study/course-hero/" element={<CourseHero />} />
        <Route path="/online-study/brainly/" element={<Brainly />} />
        <Route path="/online-study/focusmate/" element={<Focusmate />} />
        <Route path="/online-study/studystream-platform/" element={<StudyStreamPlatform />} />
        <Route path="/online-study/study-together/" element={<StudyTogether />} />
        <Route path="/online-study/coursera/" element={<Coursera />} />
        <Route path="/online-study/edx/" element={<Edx />} />
        <Route path="/online-study/udemy/" element={<Udemy />} />
        <Route path="/online-study/ypt-yeolpumta/" element={<YPTYeolPumTa />} />
        <Route path="/cn/online-study/focusmate/" element={<FocusmateCn />} />
        <Route path="/fr/online-study/focusmate/" element={<FocusmateFr />} />
        <Route path="/jp/online-study/focusmate/" element={<FocusmateJp />} />
        <Route path="/kr/online-study/focusmate/" element={<FocusmateKr />} />
        <Route path="/tw/online-study/focusmate/" element={<FocusmateTw />} />
        <Route path="/vn/online-study/focusmate/" element={<FocusmateVi />} />
        <Route path="/online-workspace/flown/" element={<Flown />} />
        <Route path="/online-workspace/gather-town/" element={<GatherTown />} />
        <Route path="/online-workspace/caveday/" element={<Caveday />} />
        <Route path="/online-workspace/cofocus/" element={<Cofocus />} />
        <Route path="/blocked-preview/" element={<BlockedPreview />} />
        <Route path="/openstudy/" element={<OpenStudy />} />
        <Route path="/openstudy/history/" element={<OpenStudyHistory />} />
        <Route path="/wunderlist/" element={<Wunderlist />} />
        <Route path="/wunderlist/history/" element={<WunderlistHistory />} />
        <Route path="/altspacevr/" element={<AltspaceVR />} />
        <Route path="/altspacevr/history/" element={<AltspaceVRHistory />} />
        <Route path="/google-wave/" element={<GoogleWave />} />
        <Route path="/google-wave/history/" element={<GoogleWaveHistory />} />
        <Route path="/hipchat/" element={<HipChat />} />
        <Route path="/hipchat/history/" element={<HipChatHistory />} />
        <Route path="/stride/" element={<Stride />} />
        <Route path="/stride/history/" element={<StrideHistory />} />
        <Route path="/sunrise-calendar/" element={<SunriseCalendar />} />
        <Route path="/sunrise-calendar/history/" element={<SunriseCalendarHistory />} />
        <Route path="/mailbox/" element={<Mailbox />} />
        <Route path="/mailbox/history/" element={<MailboxHistory />} />
        <Route path="/yahoo-groups/" element={<YahooGroups />} />
        <Route path="/yahoo-groups/history/" element={<YahooGroupsHistory />} />
        <Route path="/google-spaces/" element={<GoogleSpaces />} />
        <Route path="/google-spaces/history/" element={<GoogleSpacesHistory />} />
        <Route path="/quill/" element={<Quill />} />
        <Route path="/quill/history/" element={<QuillHistory />} />
        <Route path="/google-hangouts/" element={<GoogleHangouts />} />
        <Route path="/google-hangouts/history/" element={<GoogleHangoutsHistory />} />
        <Route path="/chalkup/" element={<Chalkup />} />
        <Route path="/chalkup/history/" element={<ChalkupHistory />} />
        <Route path="/todaysmeet/" element={<TodaysMeet />} />
        <Route path="/todaysmeet/history/" element={<TodaysMeetHistory />} />
        <Route path="/wikispaces/" element={<Wikispaces />} />
        <Route path="/wikispaces/history/" element={<WikispacesHistory />} />
        <Route path="/astrid/" element={<Astrid />} />
        <Route path="/astrid/history/" element={<AstridHistory />} />
        <Route path="/producteev/" element={<Producteev />} />
        <Route path="/producteev/history/" element={<ProducteevHistory />} />
        <Route path="/sandglaz/" element={<Sandglaz />} />
        <Route path="/sandglaz/history/" element={<SandglazHistory />} />
        <Route path="/fetchnotes/" element={<Fetchnotes />} />
        <Route path="/fetchnotes/history/" element={<FetchnotesHistory />} />
        <Route path="/iqtell/" element={<IQTell />} />
        <Route path="/iqtell/history/" element={<IQTellHistory />} />
        <Route path="/height/" element={<Height />} />
        <Route path="/height/history/" element={<HeightHistory />} />
        <Route path="/google-tables/" element={<GoogleTables />} />
        <Route path="/google-tables/history/" element={<GoogleTablesHistory />} />
        <Route path="/alternatives/" element={<Alternatives />} />
        <Route path="*" element={<NotFound />} />
        </Routes></Suspense>
      </CountryBlock>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
